import moment from 'moment-timezone'
import { logWarn } from 'lib/logging'

const _getFormat = (value: number | string | Date | null | moment.Moment, timezone?: string): string => {
  // using startOf(`day`) to avoid it returning i.e. `Today` even though it's one calendar day ago (but less than 24 hours ago)
  // with a timezone, "today" is the *location's* today, which can be a different calendar day than the viewer's
  let dateDifference = timezone
    ? moment.tz(value, timezone).startOf(`day`).diff(moment.tz(timezone).startOf(`day`), `days`)
    : moment(value).startOf(`day`).diff(moment().startOf(`day`), `days`)
  if (dateDifference === 0) {
    return `[Today at] h:mm A`
  } else if (dateDifference === -1) {
    return `[Yesterday at] h:mm A`
  }
  return `MMM D, YYYY h:mm A`
}

/**
 * Whether a zoned timestamp reads as a different wall-clock time for the viewer than it does
 * in its own zone. Compares the actual UTC offsets at that instant rather than the zone
 * names, so equivalent zones (`US/Eastern` vs `America/New_York`) count as the same.
 */
function readsDifferentlyForViewer(date: moment.Moment): boolean {
  return date.utcOffset() !== date.clone().tz(moment.tz.guess()).utcOffset()
}

export function formatDate({
  value,
  dateOnly,
  format,
  useLocalUtc,
  timezone,
}: {
  value?: number | string | Date | null | moment.Moment
  dateOnly?: boolean
  format?: string
  useLocalUtc?: boolean
  /**
   * IANA zone to render the instant in, e.g. a location's `timezone`. Takes precedence over
   * `useLocalUtc`. The zone abbreviation is appended when the viewer sits at a different
   * offset, so an out-of-state user can tell the time is the practice's, not their own.
   * Ignored when moment has no data for the zone.
   */
  timezone?: string | null
}): string | undefined {
  if (!value) {
    return ``
  }
  // The API serializes some date-only fields (e.g. Scheduler.day) through a plain GraphQL
  // String, so the stored instant arrives as raw epoch-ms digits moment cannot parse.
  if (typeof value === `string` && /^\d{13}$/.test(value)) {
    value = Number(value)
  }
  if (!moment(value).isValid()) {
    logWarn(`Date input is invalid`, value)
    return `${value}`
  }

  let date = moment.utc(value)
  const zone = timezone && moment.tz.zone(timezone) ? timezone : undefined
  if (zone) {
    date = date.tz(zone)
  } else if (useLocalUtc) {
    const localTimezone = moment.tz.guess()
    const utcOffset = moment.tz(localTimezone).utcOffset()
    date = date.utcOffset(utcOffset)
  }

  // a midnight instant is normally a date with no time attached, but an explicit `dateOnly`
  // wins: an appointment the PMS really booked at 00:00 has a time, and it is not nothing
  if (dateOnly === undefined && date.hours() === 0 && date.minutes() === 0) {
    dateOnly = true
  }

  if (dateOnly) return date.format(format || `MMM D, YYYY`)
  // `z` renders the zone abbreviation (EDT, PST); a bare date has no time to qualify, so the
  // dateOnly branch above never carries one
  const zoneSuffix = zone && readsDifferentlyForViewer(date) ? ` z` : ``
  return date.format(format || _getFormat(value, zone) + zoneSuffix)
}

/**
 * True when a date-only value falls on today's local calendar day or later.
 *
 * The API stamps date-only fields (appointment dates, hygiene due dates) at midnight UTC of
 * the intended day. Comparing those as instants reads them a day early west of UTC, and a
 * strict `isAfter` drops today as well, so an appointment scheduled for today renders as no
 * appointment at all. Compare whole calendar days instead: take the UTC `YYYY-MM-DD`, re-parse
 * it as a local start-of-day moment, and measure days against days. Same rule as `dueDateAsDay`
 * in `app/lib/taskDueDate`.
 */
export function isTodayOrLater(value?: number | string | Date | null | moment.Moment): boolean {
  if (!value) return false
  return moment(moment.utc(value).format(`YYYY-MM-DD`)).isSameOrAfter(moment().startOf(`day`))
}

export function getAge(birthDate: number | string | Date | moment.Moment) {
  let age = moment().diff(birthDate, `years`)
  return age ?? ``
}

/**
 * Recency label for a message/activity timestamp, matching the Inbox conversation list 1:1.
 * Day math is done in UTC (like the rest of the app), so it never drifts a day from the feed:
 * today → local time (h:mm A), yesterday → "Yesterday", within a week → weekday, older → "MMM D".
 */
export function formatTimestamp(value: number | string | Date | undefined | moment.Moment): string {
  const dateDifference = moment.utc(value).startOf(`day`).diff(moment().utc().startOf(`day`), `days`)
  if (dateDifference === 0) return moment.utc(value).local().format(`h:mm A`)
  if (dateDifference === -1) return `Yesterday`
  if (dateDifference > -7) return moment.utc(value).local().format(`ddd`)
  return moment.utc(value).format(`MMM D`)
}

/**
 * Appointments keep the calendar day and the clock time in separate fields: `date` is
 * date-only and `time` is a bare `HH:mm` string with no offset, meaning the wall clock at
 * the practice. Resolves the pair against the location's timezone into a real instant plus
 * the zone to render it in, ready to spread into `DateComponent` or `formatDate`.
 *
 * Without a usable timezone there is no instant to compute, so the wall clock is passed
 * through to be rendered verbatim: still the practice's time, just not labelled as such.
 *
 * @param timezone the location's IANA `timezone`, e.g. `America/New_York`
 */
export function appointmentTimestamp({
  date,
  time,
  timezone,
}: {
  date?: number | string | Date | null | moment.Moment
  time?: string | null
  timezone?: string | null
}): { value?: string; timezone?: string; dateOnly?: boolean } {
  if (!date) return {}
  const day = moment.utc(date)
  if (!day.isValid()) return {}

  const [hour, minute] = (time || ``).split(`:`)
  const wallClock = `${day.format(`YYYY-MM-DD`)}T${hour}:${minute}`
  // a missing time is `null`, not `"00:00"`, so a parseable time always means a real one
  if (!moment.utc(wallClock, `YYYY-MM-DDTHH:mm`, true).isValid()) {
    return { value: day.format(`YYYY-MM-DD`), dateOnly: true }
  }

  const zone = timezone && moment.tz.zone(timezone) ? timezone : undefined
  if (!zone) return { value: wallClock, dateOnly: false }

  return { value: moment.tz(wallClock, `YYYY-MM-DDTHH:mm`, zone).toISOString(), timezone: zone, dateOnly: false }
}
